import i18next from 'i18next';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { PaletteMode } from '@mui/material';

import { appDefaultLanguageCode } from 'Consts/language';
import { Themes } from 'Consts/themes';
import { DEFAULT_TIMEZONE } from 'Consts/timezone';
import type { CourseListViewMode } from 'Courses/pages/courses-list/CoursesListView.consts';
import { CoursesListViewConsts } from 'Courses/pages/courses-list/CoursesListView.consts';
import {
  getLanguageFromLocalStorage,
  getThemeFromLocalStorage,
  getTimezoneFromLocalStorage,
  saveLanguageToLocalStorage,
  saveThemeToLocalStorage,
  saveTimezoneToLocalStorage
} from 'Utils/localStorageUtils';

import type { ICurrentUserInfoResponse } from '@/libs/models';

import * as asyncThunks from './async-thunks';
import type { UsersState } from './types';

const initialState: UsersState = {
  currentUserInfo: {} as ICurrentUserInfoResponse,
  preferences: {
    theme: Themes.light,
    language: appDefaultLanguageCode,
    timezone: DEFAULT_TIMEZONE,
    coursePreferences: {
      viewMode: CoursesListViewConsts.defaultViewMode
    }
  },
  isSaveCurrentUserInfoLoading: false,
  isSaveCurrentUserInfoSuccess: false,
  isGetCurrentUserInfoLoading: false
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    hydratePreferencesFromLocalStorage(state) {
      const savedTheme = getThemeFromLocalStorage();
      const savedLanguage = getLanguageFromLocalStorage();
      const savedTimezone = getTimezoneFromLocalStorage();

      if (savedTheme) {
        state.preferences.theme = savedTheme;
      }
      if (savedLanguage) {
        state.preferences.language = savedLanguage;
      }
      if (savedTimezone && savedTimezone !== 'UTC') {
        state.preferences.timezone = savedTimezone;
      } else {
        state.preferences.timezone = DEFAULT_TIMEZONE;
        saveTimezoneToLocalStorage(DEFAULT_TIMEZONE);
      }
    },

    setTheme(state, action: PayloadAction<PaletteMode>) {
      state.preferences.theme = action.payload;
      saveThemeToLocalStorage(action.payload);
    },

    setLanguage(state, action: PayloadAction<string>) {
      state.preferences.language = action.payload;
      saveLanguageToLocalStorage(action.payload);
      i18next.changeLanguage(action.payload);
    },

    setTimezone(state, action: PayloadAction<string>) {
      state.preferences.timezone = action.payload;
      saveTimezoneToLocalStorage(action.payload);
    },

    setCourseViewMode(state, action: PayloadAction<CourseListViewMode>) {
      state.preferences.coursePreferences.viewMode = action.payload;
    },

    setIsStartBannerHidden(state, action: PayloadAction<boolean>) {
      state.preferences.isStartBannerHidden = action.payload;
    },

    clearCurrentUserInfo(state) {
      state.currentUserInfo = {} as ICurrentUserInfoResponse;
    },

    clearIsSaveCurrentUserInfoSuccess(state) {
      state.isSaveCurrentUserInfoSuccess = false;
    },

    clearUserData(state) {
      state.currentUserInfo = {} as ICurrentUserInfoResponse;
      state.preferences.coursePreferences = {
        viewMode: CoursesListViewConsts.defaultViewMode
      };
      state.isSaveCurrentUserInfoLoading = false;
      state.isSaveCurrentUserInfoSuccess = false;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(asyncThunks.getCurrentUserInfo.pending, state => {
        state.isGetCurrentUserInfoLoading = true;
      })
      .addCase(asyncThunks.getCurrentUserInfo.fulfilled, (state, action) => {
        state.currentUserInfo = action.payload;
        state.isGetCurrentUserInfoLoading = false;

        if (action.payload.languageCode) {
          state.preferences.language = action.payload.languageCode;
          saveLanguageToLocalStorage(action.payload.languageCode);
          i18next.changeLanguage(action.payload.languageCode);
        }

        if (action.payload.timezone && action.payload.timezone !== 'UTC') {
          state.preferences.timezone = action.payload.timezone;
          saveTimezoneToLocalStorage(action.payload.timezone);
        } else if (!action.payload.timezone) {
          state.preferences.timezone = DEFAULT_TIMEZONE;
          saveTimezoneToLocalStorage(DEFAULT_TIMEZONE);
        }
      })
      .addCase(asyncThunks.getCurrentUserInfo.rejected, state => {
        state.isGetCurrentUserInfoLoading = false;
      })
      .addCase(asyncThunks.saveCurrentUserInfo.pending, state => {
        state.isSaveCurrentUserInfoLoading = true;
        state.isSaveCurrentUserInfoSuccess = false;
      })
      .addCase(asyncThunks.saveCurrentUserInfo.fulfilled, (state, action) => {
        state.currentUserInfo.displayName = action.payload.displayName;
        state.currentUserInfo.firstName = action.payload.firstName;
        state.currentUserInfo.lastName = action.payload.lastName;
        state.currentUserInfo.avatarColor = action.payload.avatarColor;

        if (action.payload.timezone && action.payload.timezone !== 'UTC') {
          state.currentUserInfo.timezone = action.payload.timezone;
          state.preferences.timezone = action.payload.timezone;
          saveTimezoneToLocalStorage(action.payload.timezone);
        } else if (!action.payload.timezone) {
          state.currentUserInfo.timezone = DEFAULT_TIMEZONE;
          state.preferences.timezone = DEFAULT_TIMEZONE;
          saveTimezoneToLocalStorage(DEFAULT_TIMEZONE);
        }

        state.isSaveCurrentUserInfoLoading = false;
        state.isSaveCurrentUserInfoSuccess = true;
      })
      .addCase(asyncThunks.saveCurrentUserInfo.rejected, state => {
        state.isSaveCurrentUserInfoLoading = false;
        state.isSaveCurrentUserInfoSuccess = false;
      })
      .addCase(asyncThunks.saveIsStartBannerHidden.fulfilled, (state, action) => {
        state.preferences.isStartBannerHidden = action.payload;
      })
      .addCase(asyncThunks.saveAreTipsHidden.fulfilled, (state, action) => {
        state.currentUserInfo.areTipsHidden = action.payload;
      })
      .addCase(asyncThunks.saveIsMachineBarPinned.fulfilled, (state, action) => {
        state.currentUserInfo.isMachineBarPinned = action.payload;
      })
      .addCase(asyncThunks.saveLanguage.fulfilled, (state, action) => {
        state.preferences.language = action.payload;
        saveLanguageToLocalStorage(action.payload);
        i18next.changeLanguage(action.payload);
      })
      .addCase(asyncThunks.saveTheme.fulfilled, (state, action) => {
        state.preferences.theme = action.payload as PaletteMode;
        saveThemeToLocalStorage(action.payload as PaletteMode);
      })
      .addCase(asyncThunks.syncPreferencesFromUserData.fulfilled, (state, action) => {
        if (
          action.payload.theme &&
          (action.payload.theme === 'light' || action.payload.theme === 'dark')
        ) {
          state.preferences.theme = action.payload.theme as PaletteMode;
          saveThemeToLocalStorage(action.payload.theme);
        }
        if (action.payload.language) {
          state.preferences.language = action.payload.language;
          saveLanguageToLocalStorage(action.payload.language);
          i18next.changeLanguage(action.payload.language);
        }
        if (action.payload.timezone) {
          state.preferences.timezone = action.payload.timezone;
          saveTimezoneToLocalStorage(action.payload.timezone);
        }
        if (action.payload.isStartBannerHidden !== undefined) {
          state.preferences.isStartBannerHidden = action.payload.isStartBannerHidden;
        }
      });
  }
});

export const {
  hydratePreferencesFromLocalStorage,
  setTheme,
  setLanguage,
  setTimezone,
  setCourseViewMode,
  setIsStartBannerHidden,
  clearCurrentUserInfo,
  clearIsSaveCurrentUserInfoSuccess,
  clearUserData
} = userSlice.actions;

export default userSlice.reducer;
